import { Box, Typography, Card, CardContent, Button, Stack, Paper } from '@mui/material';
import { isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateFormat, getTimeFormat } from '@utils/dateHelper';

interface ReservationPlan {
  _id: string;
  name: string;
}

interface Reservation {
  _id: string;
  customerName?: string;
  start: string;
  end: string;
  status: string;
  planId?: ReservationPlan;
  discountedAmount?: number;
  remark?: string;
  [key: string]: unknown;
}

interface ConfirmCompletedListProps {
  reservations: Reservation[];
  onNoShow: (reservationId: string) => void;
  onComplete: (reservationId: string) => void;
  onReviewQr?: (reservation: Reservation) => void;
  onRefresh?: () => void;
}

const ConfirmCompletedList = ({ reservations, onNoShow, onComplete, onReviewQr, onRefresh }: ConfirmCompletedListProps) => {
  const { t } = useTranslation();

  // Filter reservations that are in the past and still confirmed
  const pastIncompleteReservations = reservations.filter(res =>
    isPast(new Date(res.start)) &&
    res.status === 'confirmed'
  );

  if (pastIncompleteReservations.length === 0) {
    return null;
  }

  return (
    <Paper elevation={3} sx={{ mb: 4, p: 2, bgcolor: '#f8f9fa' }}>
      <Typography variant="h6" gutterBottom sx={{
        color: 'primary.main',
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        pb: 1,
        mb: 2
      }}>
        {t('reservationList.pastIncompleteHeading', 'Past Sessions to Confirm')} ({pastIncompleteReservations.length})
      </Typography>

      {pastIncompleteReservations.map(reservation => (
        <Card key={reservation._id} sx={{ mb: 2, borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {reservation.customerName || ''}
                </Typography>
                <Typography variant="body2">
                  {getDateFormat(new Date(reservation.start))} ({getTimeFormat(reservation.start)} - {getTimeFormat(reservation.end)})
                </Typography>
                <Typography variant="body2">
                  {reservation.planId?.name} - {reservation.discountedAmount ?? 0} HKD
                </Typography>
                {reservation.remark && (
                  <Typography variant="body2" color="text.secondary">
                    {t('reservationList.remark')}: {reservation.remark}
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: { xs: 2, sm: 0 } }}>
                {onReviewQr && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => onReviewQr(reservation)}
                  >
                    {t('liveReview.reviewQr')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={() => {
                    onNoShow(reservation._id);
                    if (onRefresh) onRefresh();
                  }}
                >
                  {t('reservationList.customerNoShow')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => {
                    onComplete(reservation._id);
                    if (onRefresh) onRefresh();
                  }}
                >
                  {t('reservationList.completed')}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Paper>
  );
};

export default ConfirmCompletedList;
